import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { getNodesByDrawingSheet, deleteNode } from "../../services/operations/nodeAPI";
import toast from "react-hot-toast";
import { Loader2 } from "lucide-react";
import { Link } from "react-router-dom";
import { Download, Delete, ArrowLeft, FileText, Users, Eye, Trash2, Activity, Target } from "lucide-react";

const ViewNodes = () => {
  const { drawingSheetId } = useParams();
  const navigate = useNavigate();
  const [nodes, setNodes] = useState([]);
  const [drawingNumber, setDrawingNumber] = useState("");
  const [chairman, setChairman] = useState("");
  const [scribe, setScribe] = useState("");
  const [team, setTeam] = useState([]);
  const [loading, setLoading] = useState(false);

  const fetchNodes = async () => {
    try {
      setLoading(true);
      const response = await getNodesByDrawingSheet(drawingSheetId);
      if (response.success) {
        setNodes(response.nodes);
        setDrawingNumber(response.drawingNumber);
        setChairman(response.chairman);
        setScribe(response.scribe);
        setTeam(response.team);
      } else {
        toast.error(response.message || "Failed to fetch nodes");
      }
    } catch (error) {
      toast.error("Error fetching nodes");
    }
    setLoading(false);
  };

  // delete handler function
  const handleDeleteNode = async (nodeId) => {
    const confirmDelete = window.confirm("Are you sure you want to delete this node and all related worksheets?");
    if (!confirmDelete) return;

    try {
      const res = await deleteNode(nodeId);
      if (res.success) {
        toast.success("Node deleted successfully");
        fetchNodes(); // refresh node list
      } else {
        toast.error(res.message);
      }
    } catch (error) {
      toast.error("Failed to delete node");
    }
  };

  useEffect(() => {
    if (drawingSheetId) {
      fetchNodes();
    }
  }, [drawingSheetId]);

  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <Loader2 className="animate-spin h-12 w-12 text-blue-500" />
      </div>
    );
  }
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-blue-50/30 py-8 px-4">
      <div className="max-w-6xl mx-auto">
        {/* Modern Header */}
        <div className="mb-6">
          <div className="flex gap-2 items-center">
            <div className="p-2 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-2xl shadow-xl">
              <Activity className="h-6 w-6 text-white" />
            </div>
          
          <h1 className="text-2xl font-bold bg-gradient-to-r from-slate-800 to-slate-600 bg-clip-text text-transparent ">
            Nodes for Drawing: {drawingNumber}
          </h1>
          </div>
          <p className="text-slate-600 text-lg">Manage and view all nodes for this drawing</p>
        </div>

        {/* Drawing Details Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <div className="group relative overflow-hidden">
            <div className="absolute inset-0 bg-gradient-to-br from-emerald-300 to-green-300 rounded-2xl"></div>
            <div className="relative bg-white/80 backdrop-blur-sm rounded-2xl shadow-lg border border-white/20 p-6 transition-all duration-300 hover:shadow-xl hover:scale-105">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-gradient-to-br from-emerald-500 to-green-600 rounded-xl shadow-lg">
                  <Users className="h-6 w-6 text-white" />
                </div>
                <div>
                  <h3 className="text-sm font-bold text-slate-600 uppercase tracking-wider mb-1">Chairman</h3>
                  <p className="text-lg font-bold text-slate-800">{chairman}</p>
                </div>
              </div>
            </div>
          </div>

          <div className="group relative overflow-hidden">
            <div className="absolute inset-0 bg-gradient-to-br from-blue-300 to-indigo-300 rounded-2xl"></div>
            <div className="relative bg-white/80 backdrop-blur-sm rounded-2xl shadow-lg border border-white/20 p-6 transition-all duration-300 hover:shadow-xl hover:scale-105">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-xl shadow-lg">
                  <Users className="h-6 w-6 text-white" />
                </div>
                <div>
                  <h3 className="text-sm font-bold text-slate-600 uppercase tracking-wider mb-1">Scribe</h3>
                  <p className="text-lg font-bold text-slate-800">{scribe}</p>
                </div>
              </div>
            </div>
          </div>

          <div className="group relative overflow-hidden">
            <div className="absolute inset-0 bg-gradient-to-br from-purple-300 to-pink-300 rounded-2xl"></div>
            <div className="relative bg-white/80 backdrop-blur-sm rounded-2xl shadow-lg border border-white/20 p-6 transition-all duration-300 hover:shadow-xl hover:scale-105">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-gradient-to-br from-purple-500 to-pink-600 rounded-xl shadow-lg">
                  <Target className="h-6 w-6 text-white" />
                </div>
                <div>
                  <h3 className="text-sm font-bold text-slate-600 uppercase tracking-wider mb-1">Total Nodes</h3>
                  <p className="text-lg font-bold text-slate-800">{nodes.length}</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Team Members Section */}
        <div className="relative overflow-hidden mb-8">
          <div className="absolute inset-0 bg-gradient-to-br from-indigo-300 to-purple-300 rounded-2xl"></div>
          <div className="relative bg-white/80 backdrop-blur-sm rounded-2xl shadow-lg border border-white/20 p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-xl shadow-lg">
                <Users className="h-5 w-5 text-white" />
              </div>
              <h3 className="text-lg font-bold text-slate-800">Team Members</h3>
            </div>
            <div className="flex flex-wrap gap-3">
              {team.map((member, idx) => (
                <span
                  key={idx}
                  className="px-4 py-2 bg-gradient-to-r from-slate-100 to-gray-100 text-slate-700 rounded-full font-medium shadow-sm border border-white/50 transition-all duration-200 hover:scale-105"
                >
                  {member.firstName} {member.lastName}
                </span>
              ))}
            </div>
          </div>
        </div>

        {/* Nodes Table */}
        <div className="relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-slate-500/5 to-gray-600/5 rounded-2xl"></div>
          <div className="relative bg-white/80 backdrop-blur-sm rounded-2xl shadow-lg border border-black/20 overflow-hidden">
            <div className="p-6 border-b border-white/20 bg-gradient-to-r from-slate-50/50 to-white/50">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-gradient-to-br from-slate-600 to-gray-700 rounded-xl shadow-lg">
                  <FileText className="h-6 w-6 text-white" />
                </div>
                <div>
                  <h2 className="text-2xl font-bold text-slate-800">Node Management</h2>
                  <p className="text-slate-600 text-sm mt-1">View and manage nodes for this drawing</p>
                </div>
              </div>
            </div>

            {nodes.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gradient-to-r from-blue-500 to-indigo-600 text-white">
                    <tr>
                      <th className="px-6 py-4 text-left text-sm font-bold uppercase tracking-wider">
                        Node Number
                      </th>
                      <th className="px-6 py-4 text-left text-sm font-bold uppercase tracking-wider">
                        Design Intent
                      </th>
                      <th className="px-6 py-4 text-center text-sm font-bold uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-white/20">
                    {nodes.map((node) => (
                      <tr
                        key={node._id}
                        className="hover:bg-white/50 transition-all duration-300 group"
                      >
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-3">
                            <div className="p-2 bg-gradient-to-br from-blue-100 to-indigo-100 rounded-lg">
                              <Target className="h-4 w-4 text-blue-600" />
                            </div>
                            <span className="text-slate-800 font-bold text-lg">
                              Node {node.nodeNumber}
                            </span>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <p className="text-slate-600 font-medium leading-relaxed">
                            {node.designIntent}
                          </p>
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex flex-wrap gap-3 justify-center">
                            <button
                              onClick={() => navigate(`/dashboard/node/${node._id}/worksheets`)}
                              className="bg-gradient-to-r from-blue-500 to-indigo-600 hover:from-blue-600 hover:to-indigo-700 text-white px-4 py-2 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 flex items-center gap-2 transform hover:scale-105 font-semibold"
                            >
                              <Eye className="h-4 w-4" />

                              <div className="hidden md:flex lg:flex">View Worksheets</div>
                            </button>

                            <button
                              onClick={() => navigate(`/dashboard/download/${node._id}`)}
                              className="bg-gradient-to-r from-emerald-500 to-green-600 hover:from-emerald-600 hover:to-green-700 text-white px-4 py-2 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 flex items-center gap-2 transform hover:scale-105 font-semibold"
                            >
                              <Download className="h-4 w-4" />
                              <div className="hidden md:flex lg:flex">Download</div>
                            </button>

                            <button
                              onClick={() => handleDeleteNode(node._id)}
                              className="bg-gradient-to-r from-red-500 to-pink-600 hover:from-red-600 hover:to-pink-700 text-white p-2 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105"
                              title="Delete Node"
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                            
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="text-center py-16">
                <div className="p-6 bg-slate-100 rounded-2xl w-fit mx-auto mb-6">
                  <Target className="h-16 w-16 text-slate-400 mx-auto" />
                </div>
                <p className="text-slate-500 text-xl mb-2">No nodes created yet.</p>
                <p className="text-slate-400">Create your first node to get started</p>
              </div>
            )}
          </div>
        </div>

        {/* Back Button */}
        <div className="mt-8">
          <button
            onClick={() => navigate(-1)}
            className="w-full bg-gradient-to-r from-slate-600 to-gray-700 hover:from-slate-700 hover:to-gray-800 text-white py-4 px-6 rounded-2xl shadow-xl hover:shadow-2xl transition-all duration-300 flex items-center justify-center gap-3 font-bold text-lg transform hover:scale-[1.02]"
          >
            <ArrowLeft className="h-6 w-6" />
            Go Back
          </button>
        </div>
      </div>
    </div>
  );
};

export default ViewNodes;